'use client';

import '@/app/lcv/components/sections/spartan/products/shared/reveal.css';

import { type ReactNode, useEffect, useRef } from 'react';

import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { SPARTAN_REVEAL_READY_CLASS } from '@/app/lcv/components/sections/spartan/products/shared/reveal';

const IS_INVIEW_CLASS = 'is-inview';

type SpartanExternalRevealProps = {
  children: ReactNode;
  isInView: boolean;
};

/**
 * Maps a parent-owned `isInView` flag onto Spartan CSS reveal markers
 * (`.is-inview` + scoped `spartan-product-reveal-ready`) without Locomotive.
 */
export const SpartanExternalReveal = ({
  children,
  isInView,
}: SpartanExternalRevealProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion =
    useMediaQuery('(prefers-reduced-motion: reduce)', false) ?? false;

  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion) return;

    root.classList.add(SPARTAN_REVEAL_READY_CLASS);

    const removeReadyClass = () => {
      root.classList.remove(SPARTAN_REVEAL_READY_CLASS);
    };

    if (!isInView) return removeReadyClass;

    const stamp = () => {
      root
        .querySelectorAll<HTMLElement>('[data-spartan-reveal]')
        .forEach((element) => {
          element.classList.add(IS_INVIEW_CLASS);
        });
    };

    stamp();
    const mutationObserver = new MutationObserver(stamp);
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      removeReadyClass();
    };
  }, [isInView, prefersReducedMotion]);

  return <div ref={rootRef}>{children}</div>;
};
